/**
 * Alta de inquilino, en un panel lateral.
 *
 * Un solo paso, al contrario que el alta de inmueble: aquí no hay ninguna decisión que
 * condicione a las siguientes —el modo de explotación no tiene equivalente— y un asistente de
 * tres pasos para ocho campos sería un castigo.
 *
 * EL DOCUMENTO SE VALIDA MIENTRAS SE ESCRIBE, con `idDocumentError` de `@naar/shared`: el
 * MISMO código que ejecuta el servidor. Que las dos comprobaciones compartan fichero es lo que
 * impide que diverjan, y es el motivo de que esa función viva en el paquete compartido.
 *
 * Lo único obligatorio es el nombre: el resto se completa después desde la ficha. Dar de alta
 * a alguien no debería exigir tener su DNI delante.
 *
 * El MISMO panel edita, con `tenant`. Los campos son idénticos y separarlos en dos ficheros
 * solo garantizaría que uno de los dos se quedara sin el próximo arreglo. Al editar viaja el
 * `updatedAt` como bloqueo optimista: si otro ha tocado la ficha mientras tanto, el servidor
 * responde `STALE_RESOURCE` en vez de dejar que el último en guardar pise al primero.
 *
 * AL CREAR SE PUEDE RELLENAR DESDE EL CONTRATO (`ContractReader`). Lo leído pisa lo que hubiera
 * en esos campos —soltar el contrato es pedirlo expresamente, y soltar otro tiene que corregir
 * al primero—, pero no vacía los que el contrato no trae. Cada campo rellenado lleva la marca
 * «Leído del contrato» hasta que alguien lo toca: es una propuesta, no un dato revisado. Y el
 * PDF del último contrato leído BIEN se guarda como su contrato al crearlo, para que la ficha
 * nazca con el papel que respalda lo que se ha rellenado.
 *
 * AL CREAR SE ELIGE TAMBIÉN DÓNDE VIVE: inmueble y habitación, que el contrato elige solo si
 * reconoce la dirección. Por debajo es lo mismo que «Asignar habitación» desde la ficha —un
 * contrato que se activa al momento: queda de alta en ella—, pero en la MISMA petición que el
 * alta: si la habitación no se puede asignar, no se crea nada y el error sale aquí, donde se
 * puede arreglar.
 *
 * AL EDITAR TAMBIÉN SE CAMBIA DÓNDE VIVE. Si ya tiene contrato (activo o en borrador), elegir
 * otra habitación lo MUEVE allí (`POST /leases/:id/move-unit`): es corregir dónde vive, así que
 * renta, fechas y cargos emitidos se quedan como estaban. Para quitarle la habitación está
 * «Dar de baja», no dejar el campo vacío. Si no tiene contrato, se le asigna como en el alta.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type {
  ContractPartyReading,
  ContractReadingResponse,
  IdDocumentReadingResponse,
  TenantDetailResponse,
} from '@naar/shared';
import {
  ID_DOCUMENT_TYPES,
  PREFERRED_LANGUAGES,
  PREFERRED_LANGUAGE_LABELS,
  formatIban,
  ibanError,
  idDocumentError,
  normalizeIban,
  normalizeIdDocument,
} from '@naar/shared';
import { ApiError, api, queryKeys, type AvailableUnit } from '../../api/client';
import { useCan } from '../../auth/AuthContext';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Drawer } from '../../components/overlay/Drawer';
import {
  Checkbox,
  DateTextInput,
  Field,
  FormRow,
  FormSection,
  Input,
  MoneyInput,
  Select,
  Textarea,
} from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { formatShortDate } from '../../lib/format';
import { colocacion } from './calendario';
import { useLeaseInvalidation } from '../leases/LeaseActions';
import { YA_EMPEZADA } from './AssignRoomDrawer';
import { ContractReader, lecturaCorrecta } from './ContractReader';
import { IdDocumentReader } from './IdDocumentReader';
import styles from './AssignRoomDrawer.module.css';

const DOCUMENT_TYPES = [
  { value: 'dni', label: 'DNI' },
  { value: 'nie', label: 'NIE' },
  { value: 'pasaporte', label: 'Pasaporte' },
];

const LANGUAGES = PREFERRED_LANGUAGES.map((code) => ({
  value: code,
  label: PREFERRED_LANGUAGE_LABELS[code],
}));

interface FormState {
  fullName: string;
  idDocumentType: (typeof ID_DOCUMENT_TYPES)[number];
  idDocument: string;
  idDocumentExpiresAt: string;
  nationality: string;
  birthDate: string;
  email: string;
  phone: string;
  iban: string;
  mandateReference: string;
  mandateSignedAt: string;
  preferredLanguage: string;
  marketingConsent: boolean;
  notes: string;

  /** Dónde vive. No van en el cuerpo de la ficha: son su contrato (ver `save`). */
  propertyId: string;
  unitId: string;
  startDate: string;
  endDate: string;
  monthlyRent: string;
  depositAmount: string;
  signedAt: string;
}

const EMPTY: FormState = {
  fullName: '',
  idDocumentType: 'dni',
  idDocument: '',
  idDocumentExpiresAt: '',
  nationality: '',
  birthDate: '',
  email: '',
  phone: '',
  iban: '',
  mandateReference: '',
  mandateSignedAt: '',
  preferredLanguage: 'es',
  marketingConsent: false,
  notes: '',
  propertyId: '',
  unitId: '',
  startDate: '',
  endDate: '',
  monthlyRent: '',
  depositAmount: '',
  signedAt: '',
};

function fromTenant(tenant: TenantDetailResponse): FormState {
  return {
    fullName: tenant.fullName,
    idDocumentType: tenant.idDocumentType,
    idDocument: tenant.idDocument ?? '',
    idDocumentExpiresAt: tenant.idDocumentExpiresAt ?? '',
    nationality: tenant.nationality ?? '',
    birthDate: tenant.birthDate ?? '',
    email: tenant.email ?? '',
    phone: tenant.phone ?? '',
    iban: tenant.iban ? formatIban(tenant.iban) : '',
    mandateReference: tenant.mandateReference ?? '',
    mandateSignedAt: tenant.mandateSignedAt ?? '',
    preferredLanguage: tenant.preferredLanguage,
    /** El consentimiento se guarda como INSTANTE, no como booleano: importa cuándo se dio. */
    marketingConsent: tenant.marketingConsentAt !== null,
    notes: tenant.notes ?? '',
    propertyId: placedLease(tenant)?.property.id ?? '',
    unitId: placedLease(tenant)?.unit.id ?? '',
    startDate: '',
    endDate: '',
    monthlyRent: '',
    depositAmount: '',
    signedAt: '',
  };
}

/**
 * El contrato que dice dónde vive, si se puede mover: activo o en borrador. Uno rescindido o
 * renovado es histórico y no se toca desde aquí.
 */
function placedLease(tenant: TenantDetailResponse) {
  const lease = tenant.currentLease ?? tenant.draftLease;
  return lease && (lease.status === 'activo' || lease.status === 'borrador') ? lease : null;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** El día antes de cumplirse `months` meses: un contrato de 12 meses del 1/7 acaba el 30/6. */
function monthsFrom(iso: string, months: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() + months);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/**
 * «Hab. 3 (2º)», y «— ocupada» si lo está: el porqué se cuenta debajo al elegirla. La que
 * ocupa él mismo dice «— actual»: está ocupada, pero por quien se está editando.
 */
function unitLabel(unit: AvailableUnit, ownLeaseId?: string): string {
  const own = Boolean(ownLeaseId) && unit.occupiedBy?.leaseId === ownLeaseId;
  const estado = own ? ' — actual' : unit.occupiedBy ? ' — ocupada' : '';
  return `${unit.code}${unit.floor ? ` (${unit.floor})` : ''}${estado}`;
}

const DEL_CONTRATO = 'Leído del contrato: revísalo.';
const DEL_DNI = 'Leída del documento: revísala.';

const ETIQUETA_DOCUMENTO = { dni: 'DNI', nie: 'NIE', pasaporte: 'Pasaporte' } as const;

/**
 * El avalista va a las notas: la ficha no tiene campos para él y el contrato sí lo nombra.
 * «Avalista: Maria del Carmen Ortiz · DNI 87654321X · 633 44 55 66 · mcarmen@…».
 */
function lineaDelAvalista(avalista: ContractPartyReading): string {
  const documento =
    avalista.idDocument && avalista.idDocumentType
      ? `${ETIQUETA_DOCUMENTO[avalista.idDocumentType]} ${avalista.idDocument}`
      : avalista.idDocument;
  return [`Avalista: ${avalista.fullName ?? 'sin nombre'}`, documento, avalista.phone, avalista.email]
    .filter(Boolean)
    .join(' · ');
}

interface TenantFormDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Si viene, el panel edita en vez de crear. */
  tenant?: TenantDetailResponse;
  /** Se llama con el inquilino recién creado, para poder ir a su ficha. */
  onCreated?: (tenant: { id: string; fullName: string }) => void;
}


export function TenantFormDrawer({ open, onClose, tenant, onCreated }: TenantFormDrawerProps) {
  const editing = Boolean(tenant);
  const queryClient = useQueryClient();
  const invalidateLeases = useLeaseInvalidation();
  /** Dar habitación es crear un contrato: sin ese permiso, el alta se hace sin ella. */
  const canAssign = useCan('lease:create');
  /** Moverla es editar el contrato que ya tiene. */
  const canMove = useCan('lease:update');
  const lease = tenant ? placedLease(tenant) : null;
  /** Si el panel enseña «Dónde vive»: con contrato hace falta poder moverlo; sin él, crearlo. */
  const showPlacement = lease ? canMove : canAssign;
  /** Al editar con contrato, la sección solo cambia la habitación: fechas y renta son suyas. */
  const moving = Boolean(lease);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** El documento ya es de alguien de la aplicación: se enlaza su ficha en vez de un error mudo. */
  const [duplicado, setDuplicado] = useState<{ id: string; name: string; vigente: boolean } | null>(null);
  /**
   * Si el usuario ha tocado el documento en ESTA sesión del panel.
   *
   * Sin esto, abrir la ficha de alguien cuyo documento ya estaba mal guardado enseña un error
   * en rojo sobre un campo que nadie ha tocado: parece que lo acabas de escribir tú. Y pasa de
   * verdad —la siembra tiene un DNI con la letra cambiada—. Si se guarda sin arreglarlo, el
   * servidor lo rechaza igual y ese 400 sí aterriza en el campo, que es cuando toca decirlo.
   */
  const [documentTouched, setDocumentTouched] = useState(false);
  /** Cómo estaba la ficha al abrir el panel. Ver `changedFields`. */
  const initialRef = useRef<FormState>(EMPTY);
  /** La última lectura del contrato, y qué campos rellenó que nadie ha tocado todavía. */
  const [lectura, setLectura] = useState<ContractReadingResponse | null>(null);
  const [delContrato, setDelContrato] = useState<ReadonlySet<keyof FormState>>(new Set());
  /** El PDF que se guardará como su contrato: el último que se ha leído bien. */
  const [contrato, setContrato] = useState<File | null>(null);
  /**
   * La última lectura del DNI, el documento que se guardará al guardar la ficha y si la fecha de
   * nacimiento que hay en el formulario es la leída (hasta que alguien la toca).
   */
  const [lecturaDni, setLecturaDni] = useState<IdDocumentReadingResponse | null>(null);
  const [dni, setDni] = useState<File | null>(null);
  const [nacimientoDelDni, setNacimientoDelDni] = useState(false);
  /**
   * Si la renta ya la ha puesto alguien —a mano o el contrato—. Mientras no, elegir habitación
   * trae la renta de referencia de esa habitación; después, cambiar de habitación no la pisa.
   */
  const [rentaFijada, setRentaFijada] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = tenant ? fromTenant(tenant) : EMPTY;
    initialRef.current = initial;
    setForm(initial);
    setDirty(false);
    setDocumentTouched(false);
    setFieldErrors({});
    setDuplicado(null);
    setLectura(null);
    setDelContrato(new Set());
    setContrato(null);
    setLecturaDni(null);
    setDni(null);
    setNacimientoDelDni(false);
    setRentaFijada(false);
  }, [open, tenant]);

  /*
   * Se piden TODAS, libres y ocupadas, igual que en «Asignar habitación»: una habitación que no
   * aparece no se sabe por qué no aparece; ocupada, con quién y hasta cuándo, sí.
   */
  const unitsQuery = useQuery({
    queryKey: queryKeys.availableUnits({ all: true }),
    queryFn: () => api.availableUnits({ includeOccupied: true }),
    enabled: open && showPlacement,
  });
  const units = useMemo(() => unitsQuery.data?.data ?? [], [unitsQuery.data]);
  const selectedUnit = units.find((unit) => unit.id === form.unitId);
  /** Ocupada por OTRO: la suya propia sale ocupada por él y no es motivo de aviso. */
  const occupiedByOther =
    selectedUnit?.occupiedBy && selectedUnit.occupiedBy.leaseId !== lease?.id
      ? selectedUnit.occupiedBy
      : null;
  const unitChanged = Boolean(lease) && form.unitId !== lease?.unit.id;
  /** Los otros titulares del contrato: moverlo los mueve también a ellos. */
  const coTenants = lease?.tenants.filter((other) => other.id !== tenant?.id) ?? [];

  /** Los inmuebles salen de sus habitaciones: uno sin habitaciones no tiene dónde meter a nadie. */
  const propertyOptions = useMemo(() => {
    const byId = new Map(units.map((unit) => [unit.property.id, unit.property]));
    return [...byId.values()]
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
      .map((property) => ({ value: property.id, label: `${property.name} · ${property.city}` }));
  }, [units]);

  const unitOptions = useMemo(
    () =>
      units
        .filter((unit) => unit.property.id === form.propertyId)
        .sort((a, b) => a.code.localeCompare(b.code, 'es', { numeric: true }))
        .map((unit) => ({ value: unit.id, label: unitLabel(unit, lease?.id) })),
    [units, form.propertyId, lease?.id],
  );

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
    setDirty(true);
    if ('birthDate' in next) setNacimientoDelDni(false);
    // Lo que se toca a mano deja de ser «leído del contrato»: ya lo ha revisado alguien.
    setDelContrato((current) => {
      const tocados = Object.keys(next).filter((key) => current.has(key as keyof FormState));
      if (tocados.length === 0) return current;
      const resto = new Set(current);
      for (const key of tocados) resto.delete(key as keyof FormState);
      return resto;
    });
  }

  function elegirInmueble(propertyId: string) {
    // Las habitaciones son de un inmueble: cambiarlo deja sin elegir la que hubiera.
    patch({ propertyId, unitId: '' });
  }

  function elegirHabitacion(unitId: string) {
    const unit = units.find((candidate) => candidate.id === unitId);
    const next: Partial<FormState> = { unitId };
    // Moviendo un contrato que ya existe, su renta y sus fechas no se tocan.
    if (moving) {
      patch(next);
      return;
    }
    if (unit && !rentaFijada) {
      next.monthlyRent = unit.baseRent;
      next.depositAmount = unit.baseRent;
    }
    // Una estancia necesita fechas: por defecto, desde hoy y un año, como al asignarla.
    if (unit && !form.startDate) {
      const start = todayIso();
      next.startDate = start;
      if (!form.endDate) next.endDate = monthsFrom(start, 12);
    }
    patch(next);
  }

  /**
   * La fecha de nacimiento SOLO sale de aquí: el campo no tiene valor por defecto. Lo leído pisa
   * lo que hubiera —soltar el documento es pedirlo—, y el documento se guarda aunque no se haya
   * encontrado la fecha: sigue siendo su DNI.
   */
  function aplicarDni(leido: IdDocumentReadingResponse, fichero: File) {
    setLecturaDni(leido);
    setDni(fichero);
    setDirty(true);
    if (!leido.birthDate) return;
    setForm((current) => ({ ...current, birthDate: leido.birthDate! }));
    setNacimientoDelDni(true);
    setFieldErrors(({ birthDate: _descartado, ...resto }) => resto);
  }

  function aplicarContrato(leido: ContractReadingResponse, fichero: File) {
    setLectura(leido);
    /*
     * Un PDF que no es un contrato del modelo —o un escaneo— no rellena nada ni se guarda: el
     * formulario sigue diciendo lo que decía el último contrato bueno, y ese es el que se
     * guardará.
     */
    if (!lecturaCorrecta(leido)) return;
    setContrato(fichero);

    const { tenant: persona, guarantor, contract, placement } = leido;
    const next: Partial<FormState> = {};
    if (persona.fullName) next.fullName = persona.fullName;
    if (persona.idDocument) {
      next.idDocument = normalizeIdDocument(persona.idDocument);
      if (persona.idDocumentType) next.idDocumentType = persona.idDocumentType;
    }
    if (persona.phone) next.phone = persona.phone;
    if (persona.email) next.email = persona.email;
    // La cuenta no: la que pone el contrato es la de la gestora, a la que paga (`contrato.ts`).

    if (canAssign) {
      /*
       * La estancia empieza con la firma y vence el día que pone el contrato. Solo si esa fecha
       * no existe o es anterior a la firma, el servidor propone la firma más los meses que dura.
       */
      if (contract.signedAt) {
        next.startDate = contract.signedAt;
        next.signedAt = contract.signedAt;
      }
      if (contract.endDate) next.endDate = contract.endDate;
      if (contract.monthlyRent) next.monthlyRent = contract.monthlyRent;
      if (contract.depositAmount) next.depositAmount = contract.depositAmount;
      if (placement) {
        next.propertyId = placement.propertyId;
        if (placement.unitId) next.unitId = placement.unitId;
      }
    }

    const avalista = guarantor ? lineaDelAvalista(guarantor) : null;
    const rellenados = new Set(Object.keys(next) as (keyof FormState)[]);
    // El tipo va con el documento: no se cuenta aparte en el resumen ni lleva su propia marca.
    rellenados.delete('idDocumentType');
    if (avalista) rellenados.add('notes');

    /*
     * Lo que falta para la habitación que ha elegido el contrato, como si se eligiera a mano:
     * su renta de referencia y desde hoy un año. No lleva la marca del contrato: no sale de él.
     */
    const porDefecto: Partial<FormState> = {};
    const unidad = next.unitId ? units.find((unit) => unit.id === next.unitId) : undefined;
    if (unidad && !next.monthlyRent && !rentaFijada) {
      porDefecto.monthlyRent = unidad.baseRent;
      if (!next.depositAmount) porDefecto.depositAmount = unidad.baseRent;
    }
    if (unidad && !next.startDate && !form.startDate) {
      porDefecto.startDate = todayIso();
      if (!next.endDate && !form.endDate) porDefecto.endDate = monthsFrom(porDefecto.startDate, 12);
    }
    /*
     * El contrato reconoce el inmueble pero no la habitación: si es OTRO inmueble, la que
     * hubiera elegida ya no vale; si es el mismo, se respeta.
     */
    if (next.propertyId && !next.unitId && form.propertyId !== next.propertyId) {
      porDefecto.unitId = '';
    }

    setForm((current) => ({
      ...current,
      ...porDefecto,
      ...next,
      // Se añade a las notas sin duplicar: soltar el mismo contrato dos veces no lo repite.
      ...(avalista && !current.notes.includes(avalista)
        ? { notes: current.notes.trim() ? `${current.notes.trim()}\n${avalista}` : avalista }
        : {}),
    }));
    if (rellenados.size > 0) setDirty(true);
    if (next.monthlyRent) setRentaFijada(true);
    // Un documento leído se valida al momento: si la letra no cuadra, que se vea ya en rojo.
    if (next.idDocument) setDocumentTouched(true);
    setFieldErrors((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([key]) => !rellenados.has(key.replace(/^room\./, '') as keyof FormState),
        ),
      ),
    );
    /*
     * Se SUMAN a las marcas que hubiera: un segundo contrato que no trae algún dato no
     * convierte en revisado lo que dejó el primero.
     */
    setDelContrato((current) => {
      const marcas = new Set([...current, ...rellenados]);
      if (porDefecto.unitId === '') marcas.delete('unitId');
      return marcas;
    });
  }

  /** La ayuda de un campo: la marca del contrato si viene de ahí, y si no la suya. */
  const ayuda = (campo: keyof FormState, propia?: string) =>
    delContrato.has(campo) ? DEL_CONTRATO : propia;

  /**
   * Se avisa mientras se escribe, pero SOLO cuando el documento ya tiene longitud suficiente:
   * decirle «la letra de control no cuadra» a quien ha escrito dos dígitos es ruido, no ayuda.
   * El servidor lo comprueba igualmente al guardar.
   */
  const documentWarning =
    documentTouched && form.idDocument.trim().length >= 8
      ? idDocumentError(form.idDocumentType, normalizeIdDocument(form.idDocument))
      : null;

  /**
   * El documento soltado, a sus documentos, DESPUÉS de guardar la ficha: necesita a quién
   * colgarse. Va con `ocr: false` porque ya se leyó al soltarlo. Si no se puede subir, la ficha
   * no se deshace: se avisa y tiene su sitio en la pestaña Documentos. `null` si no había ninguno.
   */
  async function guardarDni(tenantId: string): Promise<boolean | null> {
    if (!dni) return null;
    const categoria = form.idDocumentType === 'pasaporte' ? 'Pasaporte' : 'Identidad';
    return api
      .uploadTenantDocument(tenantId, categoria, dni, { ocr: false })
      .then(() => true)
      .catch(() => false);
  }

  /** Como el documento: se juzga cuando la cuenta ya está entera, no a medio escribir. */
  const ibanAviso = normalizeIban(form.iban).length >= 24 ? ibanError(form.iban) : null;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        fullName: form.fullName.trim(),
        idDocumentType: form.idDocumentType,
        idDocument: form.idDocument.trim() || null,
        idDocumentExpiresAt: form.idDocumentExpiresAt || null,
        nationality: form.nationality.trim() || null,
        birthDate: form.birthDate || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        iban: normalizeIban(form.iban) || null,
        mandateReference: form.mandateReference.trim() || null,
        mandateSignedAt: form.mandateSignedAt || null,
        preferredLanguage: form.preferredLanguage,
        marketingConsent: form.marketingConsent,
        notes: form.notes.trim() || null,
      };

      if (!tenant) {
        const saved = await api.createTenant({
          ...body,
          ...(canAssign && form.unitId
            ? {
                room: {
                  unitId: form.unitId,
                  startDate: form.startDate,
                  endDate: form.endDate || null,
                  monthlyRent: form.monthlyRent,
                  depositAmount: form.depositAmount || undefined,
                  signedAt: form.signedAt || null,
                },
              }
            : {}),
        });

        /*
         * El contrato va DESPUÉS del alta porque necesita a quién colgarse. Si no se puede
         * subir, el alta no se deshace —ya existe y está bien—: se avisa, y en la pestaña
         * Documentos de su ficha tiene su sitio para soltarlo.
         */
        let contratoGuardado: boolean | null = null;
        if (contrato) {
          // Ya leído: el formulario lo ha rellenado con él, y lo que se cambió a mano se queda.
          contratoGuardado = await api
            .uploadTenantDocument(saved.id, 'Contratos', contrato, { ocr: false })
            .then(() => true)
            .catch(() => false);
        }
        const dniGuardado = await guardarDni(saved.id);
        return { saved, contratoGuardado, dniGuardado, habitacion: null };
      }

      /**
       * AL EDITAR SE MANDA SOLO LO QUE HA CAMBIADO, y no es una optimización de bytes.
       *
       * `updateTenantSchema` es `partial()`: valida únicamente los campos presentes. Mandando
       * el objeto entero, un dato que YA estaba mal guardado bloquea cualquier edición ajena a
       * él —pasa con los DNI del maquetado, que son inventados y no pasan su propia letra de
       * control: cambiar el teléfono de Laura Martín devolvía un 400 quejándose del documento,
       * que ni se había tocado—. Enviando el diff, la comprobación salta cuando de verdad
       * editas el documento, que es cuando toca.
       *
       * De propina, dos personas editando campos distintos a la vez dejan de pisarse.
       */
      const initial = initialRef.current;
      const changed = Object.fromEntries(
        Object.entries(body).filter(([key]) => {
          const field = key as keyof FormState;
          return JSON.stringify(form[field]) !== JSON.stringify(initial[field]);
        }),
      );

      /**
       * El tipo viaja SIEMPRE que viaje el documento: la letra de control depende de él, y el
       * servidor no puede comprobar un DNI sin saber que es un DNI.
       */
      if ('idDocument' in changed) changed.idDocumentType = form.idDocumentType;

      /*
       * La ficha primero y la habitación después: si la habitación falla, los datos ya están
       * guardados y el error sale en su campo para arreglarlo y volver a guardar.
       */
      const saved =
        Object.keys(changed).length > 0
          ? await api.updateTenant(tenant.id, changed, tenant.updatedAt)
          : { id: tenant.id, fullName: tenant.fullName, leaseId: null as string | null };
      // Si después falla la habitación, reintentar no vuelve a mandar lo ya guardado.
      initialRef.current = {
        ...initialRef.current,
        ...Object.fromEntries(Object.keys(changed).map((key) => [key, form[key as keyof FormState]])),
      };

      let habitacion: 'movida' | 'asignada' | null = null;
      if (lease && form.unitId && form.unitId !== lease.unit.id) {
        await api.moveLeaseUnit(lease.id, form.unitId, lease.updatedAt);
        habitacion = 'movida';
      } else if (!lease && canAssign && form.unitId) {
        await api.createLease({
          unitId: form.unitId,
          startDate: form.startDate,
          endDate: form.endDate || null,
          monthlyRent: form.monthlyRent,
          depositAmount: form.depositAmount || '0',
          signedAt: form.signedAt || null,
          tenants: [{ tenantId: tenant.id, isPrimary: true }],
          activate: true,
        });
        habitacion = 'asignada';
      }

      const dniGuardado = await guardarDni(tenant.id);
      return { saved, contratoGuardado: null, dniGuardado, habitacion };
    },
    onSuccess: ({ saved, contratoGuardado, dniGuardado, habitacion }) => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      if (tenant) void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
      // Con habitación nace un contrato: la ocupación y la habitación elegida han cambiado.
      if (saved.leaseId || habitacion) invalidateLeases();

      if (editing) {
        const donde = selectedUnit ? `${selectedUnit.property.name} · ${selectedUnit.code}` : 'la nueva habitación';
        const nombre = saved.fullName.split(' ')[0];
        toast.success(
          habitacion === 'movida'
            ? `Guardado. ${nombre} vive ahora en ${donde}.`
            : habitacion === 'asignada'
              ? `Guardado. ${nombre} queda de alta en ${donde}.`
              : dniGuardado
                ? 'Ficha actualizada, con su documento de identidad.'
                : 'Ficha actualizada.',
        );
      } else {
        const donde =
          saved.leaseId && selectedUnit
            ? ` en ${selectedUnit.property.name} · ${selectedUnit.code}`
            : '';
        const papeles = [contratoGuardado && 'su contrato', dniGuardado && 'su documento de identidad']
          .filter(Boolean)
          .join(' y ');
        // Dónde ha caído su contrato en el calendario de la habitación.
        const cuando =
          ('leasePlacement' in saved ? saved.leasePlacement : null) === 'vencido'
            ? ', con su contrato ya terminado'
            : ('leasePlacement' in saved ? saved.leasePlacement : null) === 'programado'
              ? `, que entra el ${formatShortDate(form.startDate)} (se activa solo ese día)`
              : '';
        toast.success(`«${saved.fullName}» dado de alta${donde}${cuando}${papeles ? ` con ${papeles}` : ''}.`);
        if (contratoGuardado === false) {
          toast.error(
            'El contrato no se ha podido guardar. Súbelo desde su ficha, en la pestaña Documentos.',
          );
        }
      }
      if (dniGuardado === false) {
        toast.error(
          'El documento de identidad no se ha podido guardar. Súbelo desde su ficha, en la pestaña Documentos.',
        );
      }
      setDirty(false);
      onClose();
      if (!editing) onCreated?.({ id: saved.id, fullName: saved.fullName });
    },
    onError: (error: unknown) => {
      if (!(error instanceof ApiError)) {
        toast.error('No se ha podido guardar.');
        return;
      }

      // Igual que en el alta de inmueble: el error del servidor se pinta EN EL CAMPO, que es
      // donde se puede arreglar, y no en un banner arriba que no dice a qué se refiere.
      const errors: Record<string, string> = {};
      for (const detail of error.fieldErrors) errors[detail.path] = detail.message;

      if (error.code === 'DUPLICATE_ID_DOCUMENT') {
        const payload = error.details as
          | { existingTenantId?: string; existingTenantName?: string; existingTenantHasCurrentLease?: boolean }
          | undefined;
        errors.idDocument = `Ya es el documento de «${payload?.existingTenantName ?? 'otro inquilino'}».`;
        if (payload?.existingTenantId) {
          setDuplicado({
            id: payload.existingTenantId,
            name: payload.existingTenantName ?? 'otro inquilino',
            vigente: payload.existingTenantHasCurrentLease ?? true,
          });
        }
      }
      // La habitación no está libre en esas fechas: es la habitación lo que hay que cambiar.
      if (error.code === 'UNIT_ALREADY_LEASED' || error.code === 'LEASE_DATES_OVERLAP') {
        errors['room.unitId'] = error.message;
      }
      // Al editar, los campos de la estancia nueva llegan sin el prefijo `room.` del alta.
      if (editing && !lease) {
        for (const key of ['unitId', 'startDate', 'endDate', 'monthlyRent', 'depositAmount', 'signedAt']) {
          if (errors[key]) errors[`room.${key}`] = errors[key];
        }
      }
      if (error.code === 'STALE_RESOURCE') {
        toast.error('Alguien ha modificado la ficha mientras editabas. Recarga para ver los cambios.');
      }

      setFieldErrors(errors);
      if (Object.keys(errors).length === 0) toast.error(error.message);
    },
  });

  const roomComplete = moving
    ? Boolean(form.unitId)
    : !form.unitId || Boolean(form.startDate && form.monthlyRent);
  const valid =
    form.fullName.trim().length > 0 && roomComplete && !(moving && unitChanged && occupiedByOther);

  return (
    <Drawer
      open={open}
      title={editing ? 'Editar inquilino' : 'Nuevo inquilino'}
      subtitle={
        editing
          ? tenant?.fullName
          : 'Solo el nombre es obligatorio: el resto se puede completar después desde su ficha.'
      }
      onClose={onClose}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear inquilino'}
          </Button>
        </>
      }
    >
      {!editing && (
        <FormSection
          title="Rellenar desde el contrato"
          hint="Se leen del PDF los datos del inquilino y dónde vive. Si se lee bien, al crearlo se guarda como su contrato; si sueltas otro, cuenta el último."
        >
          <ContractReader lectura={lectura} contrato={contrato} onRead={aplicarContrato} />
        </FormSection>
      )}

      <FormSection title="Identificación">
        <Field
          label="Nombre completo"
          required
          error={fieldErrors.fullName}
          help={ayuda('fullName')}
        >
          <Input
            value={form.fullName}
            onChange={(event) => patch({ fullName: event.target.value })}
            placeholder="Laura Martín"
            invalid={Boolean(fieldErrors.fullName)}
          />
        </Field>

        <FormRow>
          <Field label="Tipo de documento" required>
            <Select
              options={DOCUMENT_TYPES}
              value={form.idDocumentType}
              onChange={(event) => {
                // El dígito de control depende del TIPO: cambiarlo es tocar el documento.
                setDocumentTouched(true);
                patch({ idDocumentType: event.target.value as FormState['idDocumentType'] });
              }}
            />
          </Field>
          <Field
            /**
             * «Número de documento» y no «Documento»: al lado hay un «Tipo de documento», y dos
             * etiquetas donde una es subcadena de la otra se confunden al leerlas en voz alta y
             * son ambiguas para cualquier cosa que busque campos por su nombre.
             */
            label="Número de documento"
            optional
            error={fieldErrors.idDocument ?? documentWarning}
            help={ayuda('idDocument', 'Se guarda normalizado: sin guiones ni espacios y en mayúsculas.')}
          >
            <Input
              value={form.idDocument}
              /**
               * Se normaliza al escribir para que lo que se ve sea lo que se guarda. Sin esto,
               * `12345678-z` y `12345678Z` son dos filas distintas y el índice único
               * `(organization_id, id_document)` no detecta el duplicado.
               */
              onChange={(event) => {
                setDocumentTouched(true);
                patch({ idDocument: normalizeIdDocument(event.target.value) });
              }}
              placeholder="12345678Z"
              maxLength={20}
              invalid={Boolean(fieldErrors.idDocument ?? documentWarning)}
            />
          </Field>
        </FormRow>

        {duplicado && !editing && (
          <div className={styles.duplicado} role="note">
            <Icon name="info" size={16} />
            <span>
              <strong>{duplicado.name}</strong> ya está en la aplicación con este documento
              {duplicado.vigente
                ? '.'
                : ', sin contrato vigente (por eso no sale en «Actuales»). No hace falta darle de alta otra vez: ábrelo y asígnale la habitación.'}{' '}
              <Link to={`/inquilinos/${duplicado.id}`} onClick={onClose} className={styles.duplicadoEnlace}>
                Abrir su ficha →
              </Link>
            </span>
          </div>
        )}

        <Field label="Caduca el" optional error={fieldErrors.idDocumentExpiresAt} help="La ficha avisa cuando el documento ya ha caducado.">
          <Input
            type="date"
            value={form.idDocumentExpiresAt}
            onChange={(event) => patch({ idDocumentExpiresAt: event.target.value })}
            invalid={Boolean(fieldErrors.idDocumentExpiresAt)}
          />
        </Field>

        <FormRow>
          <Field label="Nacionalidad" optional>
            <Input
              value={form.nationality}
              onChange={(event) => patch({ nationality: event.target.value })}
              placeholder="España"
            />
          </Field>
          <Field
            label="Fecha de nacimiento"
            optional
            error={fieldErrors.birthDate}
            help={nacimientoDelDni ? DEL_DNI : 'Se rellena al subir su DNI o pasaporte, aquí debajo.'}
          >
            {/* Se escribe: el calendario del navegador pinta HOY en gris y parece un dato. */}
            <DateTextInput
              value={form.birthDate}
              onChange={(birthDate) => patch({ birthDate })}
              invalid={Boolean(fieldErrors.birthDate)}
            />
          </Field>
        </FormRow>

        <IdDocumentReader
          lectura={lecturaDni}
          documento={dni}
          numeroDelFormulario={form.idDocument}
          onRead={aplicarDni}
        />
      </FormSection>

      <FormSection title="Contacto" hint="Hace falta un correo para poder invitarle al portal.">
        <FormRow>
          <Field
            label="Correo electrónico"
            optional
            error={fieldErrors.email}
            help={ayuda('email')}
          >
            <Input
              type="email"
              value={form.email}
              onChange={(event) => patch({ email: event.target.value })}
              placeholder="laura.martin@example.com"
              invalid={Boolean(fieldErrors.email)}
            />
          </Field>
          <Field label="Teléfono" optional error={fieldErrors.phone} help={ayuda('phone')}>
            <Input
              value={form.phone}
              onChange={(event) => patch({ phone: event.target.value })}
              placeholder="611 22 33 44"
              invalid={Boolean(fieldErrors.phone)}
            />
          </Field>
        </FormRow>

        <Field label="Idioma preferido" optional>
          <Select
            options={LANGUAGES}
            value={form.preferredLanguage}
            onChange={(event) => patch({ preferredLanguage: event.target.value })}
          />
        </Field>
      </FormSection>

      {showPlacement && (
        <FormSection
          title="Dónde vive"
          hint={
            moving
              ? `Elegir otra habitación —también de otro inmueble— mueve allí su contrato. La renta, las fechas y los cobros ya emitidos no cambian.${
                  coTenants.length > 0
                    ? ` Se mueve también ${coTenants.map((other) => other.fullName).join(' y ')}, que comparte el contrato.`
                    : ''
                }`
              : editing
                ? 'Al guardar queda de alta en la habitación y se emiten sus cobros. Se puede dejar para después.'
                : 'Al crearlo queda de alta en la habitación y se emiten sus cobros. Se puede dejar para después.'
          }
        >
          <FormRow>
            <Field
              label="Inmueble"
              required={moving}
              optional={!moving}
              help={ayuda('propertyId')}
            >
              <Select
                options={propertyOptions}
                placeholder={moving ? 'Elige un inmueble…' : 'Sin habitación por ahora'}
                value={form.propertyId}
                onChange={(event) => elegirInmueble(event.target.value)}
              />
            </Field>
            <Field
              label="Habitación"
              required={moving || Boolean(form.propertyId)}
              optional={!moving && !form.propertyId}
              error={fieldErrors['room.unitId']}
              help={
                ayuda('unitId') ??
                (unitChanged && lease
                  ? `Ahora en ${lease.property.name} · ${lease.unit.code}.`
                  : undefined)
              }
            >
              <Select
                options={unitOptions}
                placeholder={form.propertyId ? 'Elige una habitación…' : 'Elige antes el inmueble'}
                value={form.unitId}
                onChange={(event) => elegirHabitacion(event.target.value)}
                disabled={!form.propertyId}
                invalid={Boolean(fieldErrors['room.unitId'])}
              />
            </Field>
          </FormRow>

          {unitsQuery.isSuccess && units.length === 0 && (
            <div className={styles.warning}>
              <Icon name="info" size={15} />
              <span>
                Todavía no hay habitaciones. Créalas desde la pestaña «Habitaciones» de un
                inmueble; mientras, se le puede dar de alta sin ella.
              </span>
            </div>
          )}

          {/*
            Ocupada no se bloquea: se mira el CALENDARIO. Una estancia que no se pisa con la de
            quien vive ahí cabe —antes (un contrato antiguo) o después (queda programada)—. El
            servidor tiene la última palabra sobre las fechas.
          */}
          {occupiedByOther && (
            <div className={styles.warning}>
              <Icon name="info" size={15} />
              <span>
                {moving
                  ? `Esta habitación está ocupada${occupiedByOther.tenantName ? ` por ${occupiedByOther.tenantName}` : ''}${
                      occupiedByOther.until ? ` hasta el ${formatShortDate(occupiedByOther.until)}` : ' sin fecha de salida'
                    }. No se puede mover aquí mientras lo esté.`
                  : colocacion(occupiedByOther, form.startDate, form.endDate, todayIso()).texto}
              </span>
            </div>
          )}

          {form.unitId && !moving && (
            <>
              <FormRow>
                <Field
                  label="Desde"
                  required
                  error={fieldErrors['room.startDate']}
                  help={
                    ayuda('startDate') ??
                    (form.startDate && form.startDate < todayIso() ? YA_EMPEZADA : undefined)
                  }
                >
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(event) => patch({ startDate: event.target.value })}
                    invalid={Boolean(fieldErrors['room.startDate'])}
                  />
                </Field>
                <Field
                  label="Hasta"
                  optional
                  error={fieldErrors['room.endDate']}
                  help={ayuda('endDate', 'En blanco, indefinida.')}
                >
                  <Input
                    type="date"
                    value={form.endDate}
                    onChange={(event) => patch({ endDate: event.target.value })}
                    invalid={Boolean(fieldErrors['room.endDate'])}
                  />
                </Field>
              </FormRow>

              <FormRow>
                <Field
                  label="Renta mensual"
                  required
                  error={fieldErrors['room.monthlyRent']}
                  help={ayuda('monthlyRent')}
                >
                  <MoneyInput
                    value={form.monthlyRent}
                    onValueChange={(value) => {
                      setRentaFijada(true);
                      patch({ monthlyRent: value });
                    }}
                    onChange={() => undefined}
                    invalid={Boolean(fieldErrors['room.monthlyRent'])}
                  />
                </Field>
                <Field
                  label="Fianza"
                  optional
                  error={fieldErrors['room.depositAmount']}
                  help={ayuda('depositAmount', 'Normalmente una mensualidad.')}
                >
                  <MoneyInput
                    value={form.depositAmount}
                    onValueChange={(value) => {
                      setRentaFijada(true);
                      patch({ depositAmount: value });
                    }}
                    onChange={() => undefined}
                    invalid={Boolean(fieldErrors['room.depositAmount'])}
                  />
                </Field>
                <Field
                  label="Firmado el"
                  optional
                  error={fieldErrors['room.signedAt']}
                  help={ayuda('signedAt')}
                >
                  <Input
                    type="date"
                    value={form.signedAt}
                    onChange={(event) => patch({ signedAt: event.target.value })}
                    invalid={Boolean(fieldErrors['room.signedAt'])}
                  />
                </Field>
              </FormRow>
            </>
          )}
        </FormSection>
      )}

      <FormSection title="Domiciliación y RGPD">
        <Field
          label="Cuenta bancaria (IBAN)"
          optional
          error={fieldErrors.iban ?? ibanAviso}
          /**
           * La cuenta ENTERA del inquilino, escrita a mano: el contrato no la trae (la que pone es
           * la de la gestora). Se guarda normalizada y de ella sale la terminación que busca el
           * extracto para reconocer sus pagos (si varios inquilinos comparten cuenta, esa
           * terminación no identifica a nadie y lo hace su nombre; ver `extracto/identificar.ts`).
           */
          help={ayuda(
            'iban',
            !form.iban && tenant?.ibanLast4
              ? `De antes solo se guardó su terminación, ····${tenant.ibanLast4}. Escribe la cuenta entera.`
              : 'La suya, desde la que paga. La del contrato no vale: es la de la gestora.',
          )}
        >
          <Input
            value={form.iban}
            onChange={(event) => patch({ iban: event.target.value.toUpperCase() })}
            placeholder="ES78 2095 0553 5091 2493 0394"
            maxLength={42}
            autoComplete="off"
            invalid={Boolean(fieldErrors.iban ?? ibanAviso)}
          />
        </Field>

        <FormRow>
          <Field label="Referencia del mandato" optional error={fieldErrors.mandateReference}>
            <Input
              value={form.mandateReference}
              onChange={(event) => patch({ mandateReference: event.target.value })}
              placeholder="MND-2026-001"
              maxLength={60}
              invalid={Boolean(fieldErrors.mandateReference)}
            />
          </Field>
          <Field
            label="Firmado el"
            optional
            error={fieldErrors.mandateSignedAt}
            help="Un mandato firmado necesita su referencia."
          >
            <Input
              type="date"
              value={form.mandateSignedAt}
              onChange={(event) => patch({ mandateSignedAt: event.target.value })}
              invalid={Boolean(fieldErrors.mandateSignedAt)}
            />
          </Field>
        </FormRow>

        <Checkbox
          checked={form.marketingConsent}
          onChange={(event) => patch({ marketingConsent: event.target.checked })}
          label="Consiente recibir comunicaciones comerciales"
        />
      </FormSection>

      <FormSection title="Notas">
        <Field
          label="Notas internas"
          optional
          error={fieldErrors.notes}
          help={delContrato.has('notes') ? 'Se ha añadido el avalista del contrato: revísalo.' : undefined}
        >
          <Textarea
            value={form.notes}
            onChange={(event) => patch({ notes: event.target.value })}
            rows={3}
            placeholder="Lo que convenga recordar de este inquilino."
            invalid={Boolean(fieldErrors.notes)}
          />
        </Field>
      </FormSection>
    </Drawer>
  );
}
